import type { CombinedError } from "urql"
import { useMutation } from "urql"

import type {
  ProductTest,
  ProductTestMutationUpdateArgs,
  ProductTestUpdateFailure,
  ProductTestUpdateInput,
  UpdateProductTestMutation,
} from "@/graphql/codegen/graphql"
import { UpdateProductTestDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseUpdateProductTestProps {
  errorMessage?: string
  onSuccess?: () => void
}

interface UseUpdateProductTestReturn {
  update: (input: ProductTestUpdateInput) => Promise<ProductTest>
  isUpdating: boolean
  error?: CombinedError
}

export const useUpdateProductTest = (props?: UseUpdateProductTestProps): UseUpdateProductTestReturn => {
  const { errorMessage = "Failed to update product test.", onSuccess } = props ?? {}
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [{ fetching, error }, executeMutation] = useMutation<UpdateProductTestMutation, ProductTestMutationUpdateArgs>(
    UpdateProductTestDocument
  )

  const update = async (input: ProductTestUpdateInput): Promise<ProductTest> => {
    const { data, error } = await executeMutation({ input })
    const output = data?.productTest.update
    const hasError = error || output?.__typename === "ProductTestUpdateFailure"

    if (hasError) {
      const resolvedErrorMessage = error?.message ?? (output as ProductTestUpdateFailure).error.message ?? errorMessage
      notify({
        type: NotificationType.Error,
        message: resolvedErrorMessage,
      })
      throw new Error(resolvedErrorMessage)
    }

    onSuccess?.()
    return output!.productTest as ProductTest
  }

  return {
    update,
    isUpdating: fetching,
    error,
  }
}
